import { createExecutiveBriefingContext } from "./executiveBriefingContext";
import { EXECUTIVE_BRIEFING_PROVIDERS } from "./providers";
import { CLOSING_PROVIDER_ID } from "./providers/closingProvider";
import { GREETING_PROVIDER_ID } from "./providers/greetingProvider";

/**
 * Loads provider data, composes eligible sections, and returns the spoken briefing text.
 * @param {import("./executiveBriefingContext").ExecutiveBriefingInput} input
 * @returns {Promise<string>}
 */
export async function buildExecutiveBriefing(input) {
  const ctx = createExecutiveBriefingContext(input);
  const eligible = EXECUTIVE_BRIEFING_PROVIDERS.filter((provider) =>
    provider.isEligible(ctx),
  );

  const dataProviders = eligible.filter((provider) => provider.loadData);

  await Promise.all(
    dataProviders.map(async (provider) => {
      try {
        ctx.data[provider.id] = await provider.loadData(ctx);
      } catch (error) {
        console.error(
          `Executive briefing provider "${provider.id}" failed:`,
          error,
        );
        ctx.data[provider.id] = null;
      }
    }),
  );

  const closingProvider = eligible.find(
    (provider) => provider.id === CLOSING_PROVIDER_ID,
  );
  const contentProviders = eligible.filter(
    (provider) => provider.id !== CLOSING_PROVIDER_ID,
  );

  const sections = [];
  ctx.hasBriefingBody = false;

  for (const provider of contentProviders) {
    const section = provider.buildSection(ctx, ctx.data[provider.id]);
    if (!section) {
      continue;
    }

    if (provider.id !== GREETING_PROVIDER_ID) {
      ctx.hasBriefingBody = true;
    }

    sections.push(section);
  }

  if (closingProvider) {
    const closing = closingProvider.buildSection(ctx, ctx.data[closingProvider.id]);
    if (closing) {
      sections.push(closing);
    }
  }

  return sections.join(" ");
}

/**
 * Builds the briefing, stores it for replay, and triggers Ashley speech.
 * @param {import("./executiveBriefingContext").ExecutiveBriefingInput} input
 * @returns {Promise<string>}
 */
export async function deliverExecutiveBriefing(input) {
  const briefingText = await buildExecutiveBriefing(input);

  console.log("ASHLEY BRIEFING:", briefingText);
  window.lastAshleyBriefing = briefingText;
  window.dispatchEvent(new CustomEvent("ashley-briefing-start"));

  return briefingText;
}
